import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import { useScopedReport, fetchRowsPage } from "@/hooks/useScopedReport";
import { ReportRowsDialog, useRowsRequest } from "@/components/ReportRowsDialog";
import { ReportScopeBanner } from "@/components/ReportScopeBanner";

const backend = process.env.REACT_APP_BACKEND_URL;
export const ORIGIN_ROWS = [
  ["ai_direct", "AI voice agent (direct)"], ["self_booking", "Customer self-service"], ["employee", "Employee"], ["leadership_admin", "Leadership / admin"],
  ["ai_assisted", "Employee after an AI handoff (AI-assisted)"], ["unknown_legacy", "Unknown / legacy (no stamped origin) - not zero, not known"], ["unrecognised", "Unrecognised origin value"],
];

function Population({ pop, name, onRows }) {
  return (
    <div className="border rounded p-3 space-y-2" data-testid={`ba-pop-${name}`}>
      <p className="text-sm font-semibold">{name === "newly_booked" ? "Bookings created in the period" : "Consultations scheduled in the period"}</p>
      <p className="text-[11px] text-muted-foreground">{name === "newly_booked" ? "Counted by the moment the booking was made (who booked, when)." : "Counted by the consultation's current scheduled time (what is on the diary)."}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <span>Bookings (gross activity)</span><button className="font-mono text-right underline" data-testid={`ba-${name}-gross`} onClick={() => onRows(name, { group: "gross" }, "All bookings")}>{pop.gross}</button>
        <span>Currently valid</span><button className="font-mono text-right underline" data-testid={`ba-${name}-valid`} onClick={() => onRows(name, { group: "valid" }, "Currently valid bookings")}>{pop.valid}</button>
        <span>Cancelled</span><button className="font-mono text-right underline" data-testid={`ba-${name}-cancelled`} onClick={() => onRows(name, { group: "cancelled" }, "Cancelled bookings")}>{pop.cancelled}</button>
        <span title="Pending (not yet confirmed) or a duplicate the booking gate suppressed: never a booking, nothing was sent">Not bookings (pending / suppressed)</span>
        <button className="font-mono text-right underline" data-testid={`ba-${name}-notbookings`} onClick={() => onRows(name, { group: "not_bookings" }, "Not bookings")}>{pop.not_bookings.total} ({pop.not_bookings.pending} / {pop.not_bookings.suppressed})</button>
      </div>
      <p className="text-xs text-muted-foreground">By who made the booking (durable stamped origin only; the lines add up to the {pop.gross} bookings):</p>
      <div className="space-y-0.5" data-testid={`ba-${name}-origins`}>
        {ORIGIN_ROWS.map(([k, label]) => (
          <div key={k} className="flex justify-between text-sm border rounded px-2 py-0.5">
            <span>{label}</span>
            <button className="font-mono text-xs underline" data-testid={`ba-${name}-origin-${k}`} onClick={() => onRows(name, { origin: k, group: "gross" }, label)}>{pop.origin[k]}</button>
          </div>
        ))}
      </div>
      <p className="text-xs" data-testid={`ba-${name}-reconciles`}>
        {Object.values(pop.reconciles).every(Boolean) ? "Reconciles: rows = bookings + not-bookings; bookings = valid + cancelled = sum of the origin lines; every appointment counted once." : "DOES NOT RECONCILE - report this."}
      </p>
      {pop.family_scope && (
        <p className="text-xs text-muted-foreground" data-testid={`ba-${name}-outside`}>
          Outside this scope (not added above): other products {pop.outside_family.other_products_gross}, no product {pop.outside_family.product_unresolved_gross}; all products {pop.outside_family.all_products_gross}.
        </p>
      )}
    </div>
  );
}

// R17 - bookings by WHO MADE THEM for the SAME period and product scope as the rest of the page (the single-day panel below stays as a deliberate one-day override).
export function BookingActivityPanel({ qs }) {
  const { data, error, loading, reload } = useScopedReport("/reports/booking-activity", qs);
  const [req, setReq, closeReq] = useRowsRequest(qs, "booking-activity");
  const openRows = (population, extra, title) => setReq({ title: `${population === "newly_booked" ? "Created in the period" : "Scheduled in the period"} - ${title}`,
    note: "The exact appointments behind this number, for the same period and product scope.", fetchPage: (p) => fetchRowsPage("/reports/booking-activity/drilldown", qs, { population, ...extra }, p) });
  return (
    <Card data-testid="booking-activity-panel"><CardContent className="pt-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold mr-auto">Who made the bookings — AI, customers, employees, leadership, unknown</p>
        <a href={`${backend}/api/reports/booking-activity/export?${qs}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="ba-export"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />Export all rows</Button></a>
      </div>
      {error ? (
        <div role="alert" className="text-sm" data-testid="ba-error">{error} <Button size="sm" variant="outline" onClick={reload}>Try again</Button></div>
      ) : loading || !data ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : (
        <>
          <ReportScopeBanner scope={data.scope} filters={data.filters} testId="ba-scope" />
          {!data.coverage.complete && <p role="alert" className="text-xs text-amber-700" data-testid="ba-incomplete">{data.coverage.statement}</p>}
          <div className="grid md:grid-cols-2 gap-3">
            <Population pop={data.populations.newly_booked} name="newly_booked" onRows={openRows} />
            <Population pop={data.populations.scheduled} name="scheduled" onRows={openRows} />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="ba-overlap">
            {data.overlap.in_both_populations} appointment(s) are in both lists; {data.overlap.distinct_union} distinct in total. A reschedule moves the same appointment — it is never a new booking
            ({data.reschedule_events_in_window_not_added_to_any_total} reschedule event(s) in the period, added to nothing).
          </p>
        </>
      )}
      <ReportRowsDialog request={req} onClose={closeReq} />
    </CardContent></Card>
  );
}
